import * as React from 'react';
import { useContext } from 'react';
import { useTheme } from '../theme';
import cn from 'classnames';
import { InfoMessagePositionsEnum, InfoMessage, InfoMessageTypeEnum, SearchParametersFragment } from '@websky/graphql';
import Notification, { Props as NotificationProps } from '../BaseComponents/Notification';
import { InfoMessagesContext } from './context';
import { filterMessageByPosition } from './utils';
import PromoNotification from '../Notification/components/PromoNotification/PromoNotification';
import { BUSINESS_RULES_CHECKBOXES_CODES } from '@shared/config/constants/app';
import { reachGoal, InfoMessageGoal } from '../analytics';

const STATUS_MAP: Record<InfoMessageTypeEnum, NotificationProps['status']> = {
	[InfoMessageTypeEnum.Warning]: 'error',
	[InfoMessageTypeEnum.Promo]: 'note',
	[InfoMessageTypeEnum.Info]: 'warning',
	[InfoMessageTypeEnum.SubscriptionSaving]: 'subscription',
	[InfoMessageTypeEnum.HtmlContent]: 'note'
};

interface Props {
	position: InfoMessagePositionsEnum | InfoMessagePositionsEnum[];
	className?: string;
	notificationsClassName?: string;
	itemHtmlClassName?: string;
	itemClassName?: string;
	htmlClassName?: string;
	contentClassName?: string;
	messages?: InfoMessage[]; // передаем messages либо через пропсы, либо через InfoMessagesContext
	renderMessagesWithoutWrapper?: boolean;
	searchParameters?: SearchParametersFragment;
	baseOrder?: number;
}

const ALWAYS_FILTER_CODES = [...BUSINESS_RULES_CHECKBOXES_CODES, 'chamomile'];

const InfoMessages: React.FunctionComponent<Props> = props => {
	const { InfoMessages: theme } = useTheme('InfoMessages');

	const messagesFromContext = useContext(InfoMessagesContext)?.messages;

	const messages = (props.messages ?? messagesFromContext)
		?.filter(message => filterMessageByPosition(props.position, message))
		.filter(({ code }) => !ALWAYS_FILTER_CODES.includes(code));

	if (!messages?.length) {
		return null;
	}

	const renderNotification = (message: InfoMessage, index: number) => {
		const itemStyle = props.baseOrder ? { order: props.baseOrder + index } : undefined;

		if (props.searchParameters && message.code === 'recommended' && message.type === 'Promo') {
			return (
				<PromoNotification
					searchParams={props.searchParameters}
					key={index}
					title={message.title}
					image={message.image}
					status={message.type}
					infoMessage={message}
					type={'context'}
				/>
			);
		}

		const isHtmlContent = message.type === InfoMessageTypeEnum.HtmlContent;
		const renderMessagesWithoutWrapper = props.renderMessagesWithoutWrapper || isHtmlContent;

		return (
			<Notification
				renderMessagesWithoutWrapper={renderMessagesWithoutWrapper}
				key={index}
				title={message.title}
				image={message.image}
				text={message.text}
				code={message.code}
				asHtml={isHtmlContent}
				status={STATUS_MAP[message.type] ?? 'note'}
				type="global"
				className={cn(theme.notification, props.itemClassName, {
					[props.itemHtmlClassName]: isHtmlContent,
					[theme.notification__html]: isHtmlContent
				})}
				contentClassName={cn(theme.notification__content, props.contentClassName)}
				htmlClassName={props.htmlClassName}
				style={itemStyle}
			/>
		);
	};

	React.useLayoutEffect(() => {
		const transfer = document?.querySelector('div.BookingAncillaries__root_transfer');

		if (!transfer) {
			return () => {};
		}

		reachGoal(InfoMessageGoal.PurchaseTransferShow);

		const button = document?.querySelector(
			'div.BookingAncillaries__root_transfer button.BookingAncillaries__button'
		);
		if (!button) {
			return () => {};
		}

		const buttonHandler = () => {
			reachGoal(InfoMessageGoal.PurchaseTransferClick);
		};
		if (button) {
			button.addEventListener('click', buttonHandler);
		}

		return () => {
			button.removeEventListener('click', buttonHandler);
		};
	}, []);

	if (props.renderMessagesWithoutWrapper) {
		return <>{messages.map(renderNotification)}</>;
	}

	return (
		<div className={cn(theme.container, props.className)}>
			<div className={cn(theme.notifications, props.notificationsClassName)}>
				{messages.map(renderNotification)}
			</div>
		</div>
	);
};

export default InfoMessages;
